import type { ReceiptLocalePreferenceDTO } from './accountTypes.js';

export const RECEIPT_LOCALE_CODES = ['cs', 'en', 'sk'] as const;

export type ReceiptLocaleCode = (typeof RECEIPT_LOCALE_CODES)[number];

export type ReceiptLocalePreferenceMode = 'default' | 'custom';

export interface ReceiptLocalePreferenceUiState {
  readonly mode: ReceiptLocalePreferenceMode;
  readonly customLocale: ReceiptLocaleCode;
}

/** PUT body for receipt-locales preference. */
export type ReceiptLocalePutBody =
  | { mode: 'default'; uiLocale: ReceiptLocaleCode }
  | { mode: 'custom'; locales: [ReceiptLocaleCode] };

export function isReceiptLocaleCode(value: string | undefined): value is ReceiptLocaleCode {
  return value === 'cs' || value === 'en' || value === 'sk';
}

/** Map i18n language (e.g. `en-US`, `sk`) to a receipt locale code. */
export function resolveReceiptUiLocale(language: string | undefined): ReceiptLocaleCode {
  if (language === undefined || language.length === 0) {
    return 'cs';
  }
  if (language.startsWith('en')) {
    return 'en';
  }
  if (language.startsWith('sk')) {
    return 'sk';
  }
  return 'cs';
}

/**
 * Normalize stored/API uiLocale to an allowed receipt code, or null.
 * Mirrors BE `normalizeReceiptUiLocale`.
 */
export function normalizeReceiptUiLocale(
  value: string | null | undefined,
): ReceiptLocaleCode | null {
  if (value === undefined || value === null) {
    return null;
  }
  return isReceiptLocaleCode(value) ? value : null;
}

/**
 * Server-effective Default locale (generation SoT / GET `locales[0]`):
 * `normalize(uiLocale) ?? 'cs'`. For generation-aligned reads — UI Default checkboxes
 * use live `resolveReceiptUiLocale` / `receiptPreferenceEffectiveLocale` instead.
 */
export function serverEffectiveDefaultLocale(
  data: ReceiptLocalePreferenceDTO | undefined,
): ReceiptLocaleCode {
  if (data === undefined) {
    return 'cs';
  }
  return normalizeReceiptUiLocale(data.uiLocale) ?? 'cs';
}

function isDefaultDto(data: ReceiptLocalePreferenceDTO | undefined): boolean {
  if (data === undefined) {
    return true;
  }
  if (data.mode === 'default' || data.isPlatformDefault) {
    return true;
  }
  if (data.mode === 'custom') {
    return false;
  }
  return data.isPlatformDefault;
}

/**
 * Default is a mode (follow UI), never inferred from locales[0] === uiLocale.
 * Custom is only an explicit fixed locale from the server.
 * Mode comes only from `mode` / `isPlatformDefault` — not from a locale snapshot.
 */
export function receiptPreferenceFromDto(
  data: ReceiptLocalePreferenceDTO | undefined,
  uiLocale: ReceiptLocaleCode,
): ReceiptLocalePreferenceUiState {
  if (isDefaultDto(data)) {
    return { mode: 'default', customLocale: uiLocale };
  }
  const firstLocale = data?.locales[0];
  if (isReceiptLocaleCode(firstLocale)) {
    return { mode: 'custom', customLocale: firstLocale };
  }
  return { mode: 'custom', customLocale: uiLocale };
}

export function receiptPreferenceToPutBody(
  state: ReceiptLocalePreferenceUiState,
  uiLocale: ReceiptLocaleCode,
): ReceiptLocalePutBody {
  if (state.mode === 'default') {
    return { mode: 'default', uiLocale };
  }
  return { mode: 'custom', locales: [state.customLocale] };
}

/** Effective single locale the UI highlights / generation should use for this state. */
export function receiptPreferenceEffectiveLocale(
  state: ReceiptLocalePreferenceUiState,
  uiLocale: ReceiptLocaleCode,
): ReceiptLocaleCode {
  return state.mode === 'default' ? uiLocale : state.customLocale;
}

export function receiptPreferencePutBodiesEqual(
  left: ReceiptLocalePutBody,
  right: ReceiptLocalePutBody,
): boolean {
  if (left.mode !== right.mode) {
    return false;
  }
  if (left.mode === 'default' && right.mode === 'default') {
    return left.uiLocale === right.uiLocale;
  }
  if (left.mode === 'custom' && right.mode === 'custom') {
    return left.locales[0] === right.locales[0];
  }
  return false;
}

/** @deprecated Prefer receiptPreferenceToPutBody — kept for transitional callers. */
export function receiptPreferenceToLocales(
  state: ReceiptLocalePreferenceUiState,
  uiLocale: ReceiptLocaleCode,
): string[] {
  return [receiptPreferenceEffectiveLocale(state, uiLocale)];
}

export function receiptPreferenceLocalesEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((locale, index) => locale === right[index]);
}
